<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { RadioButton } from '@progress/kendo-vue-inputs'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import type { IAddressVerificationListItem, IAddress } from '../types/CountryCultureTypes'
import { ref } from 'vue'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('VerifyAddressPopup.vue')


const selectedVerifiedAddress = ref<number>(0)
const errorShow = ref(false)

const props = defineProps<{
  enteredAddress: IAddress
  verifyAddress: IAddressVerificationListItem[]
  verifyAddressError: boolean
}>()

const emit = defineEmits(["useEnteredAddress", "useSelectedAddress", "reEnterAddress"]);

function radioBtnChange(selectedAddressId: number) {
    selectedVerifiedAddress.value = selectedAddressId
    logger.debug('radioBtnChange: ' + selectedAddressId)
}

function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')
  emit('useEnteredAddress')
}

function useSelectedAddressClick() {
  logger.debug('useSelectedAddressClick')

  if (selectedVerifiedAddress.value > 0) {
    const selectedAddress = props.verifyAddress.find(
      address => address.addressId === selectedVerifiedAddress.value,
    )

    if(selectedAddress != undefined)
    {
      emit('useSelectedAddress', {
        addressLine1: selectedAddress.addressLine1,
        addressLine2: selectedAddress.addressLine2,
        houseNumber: selectedAddress.houseNumber || props.enteredAddress.houseNumber,
        city: selectedAddress.city,
        stateCode: selectedAddress.stateCode,
        postalCode: selectedAddress.postalCode,
        countryCode: selectedAddress.countryCode,
      })
    }
  }
  else
  {
    errorShow.value = !errorShow.value
  }
}

function reEnterAddressClick() {
  logger.debug('reEnterAddressClick')
  emit('reEnterAddress')
}

</script>
<template>
  <div>
    <div class="row mt-3">
        <div class="col-12">
          {{ t('checkout.CheckoutAddressValHdr') }}
          <br/> {{ t('checkout.CheckoutAddressValSubHdr') }}
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          {{ t('checkout.CheckoutAddressValAddressEntered') }}
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 enteredAddress">
          {{ props.enteredAddress.addressLine1 }}
        </div>
        <div class="col-12 enteredAddress">
          {{ props.enteredAddress.houseNumber }}
        </div>
        <div class="col-12 enteredAddress">
          {{ props.enteredAddress.addressLine2 }}
        </div>
        <div class="col-12 enteredAddress">
          {{ props.enteredAddress.city }}<template v-if="props.enteredAddress.stateCode && props.enteredAddress.stateCode !== 'N/A'">,&nbsp;{{ props.enteredAddress.stateCode }}</template>&nbsp;{{ props.enteredAddress.postalCode }}
        </div>
      </div>
      <div v-if="props.verifyAddressError && props.verifyAddress.length == 0" class="row mt-3">
        <div class="col-12">
          {{ t('checkout.CheckoutAddressValError') }}
        </div>
      </div>
      <div v-if="props.verifyAddressError && props.verifyAddress.length > 0" class="row mt-3">
        <div class="col-12">
          {{ t('checkout.CheckoutAddressValVerifiedAddress') }}
        </div>
      </div>
      <div v-if="props.verifyAddressError && props.verifyAddress.length > 0" class="row mt-3">
        <div class="col-12">
          <div
          class="verifiedAddresses"
          v-for="add in verifyAddress"
          :key="add.addressId"
          >
          <RadioButton
            :name="'group1'"
            :value="add.addressId"
            size="large"
            @change="radioBtnChange(add.addressId)"
            >{{add.addressLine1}}
            <div class="verifyaddressLine" v-if="(add.houseNumber || props.enteredAddress.houseNumber)">{{add.houseNumber || props.enteredAddress.houseNumber}}</div>
            <div class="verifyaddressLine" v-if="add.addressLine2 != undefined && add.addressLine2.length > 0">{{add.addressLine2}}</div>
            <div class="verifyaddressLine">{{add.city}}<template v-if="add.stateCode && add.stateCode !== 'N/A'">, {{add.stateCode}}</template> {{add.postalCode}}</div>
          </RadioButton>
          </div>
        </div>
      </div>
      <div v-if="errorShow" class="row mt-3">
        <div class="col-12 errorSelctedAddress">
          {{ t('checkout.CheckoutAddressValWarning') }}
        </div>
      </div>
      <div class="btnVerifyRow mt-3">
        <KButton
          class="btnUseAddress"
          :rounded="'large'"
          :theme-color="'checkoutContinue'"
          @click="useEnteredAddressClick"
          >{{t('labels.CheckoutContinue')}}</KButton
        >
        <KButton
          v-if="props.verifyAddressError && props.verifyAddress.length > 0"
          class="btnSelectAddressRadio"
          :rounded="'large'"
          :theme-color="'primary'"
          @click="useSelectedAddressClick"
          >{{t('labels.CheckoutUseSelectedAddress')}}</KButton
        >
        <KButton
          v-if="props.verifyAddressError && props.verifyAddress.length == 0"
          class="btnReEnterAddress"
          :rounded="'large'"
          :theme-color="'primary'"
          @click="reEnterAddressClick"
          >{{t('labels.CheckoutReEnterAddress')}}</KButton
        >
      </div>
  </div>
</template>

<style lang="scss" scoped>
.verifiedAddresses {
  input {
    margin-right: $spacing-2;
  }
}

/* Flex row: equal width AND equal height for both buttons regardless of text length */
.btnVerifyRow {
  display: flex;
  align-items: stretch;
  gap: $gap-sm;
}

/* Consistent button appearance — matches guest grid button pattern (guestSendEmailBtn / btnPlaceOrder) */
.btnUseAddress,
.btnSelectAddressRadio,
.btnReEnterAddress {
  /* !important required: override Kendo button defaults for layout and text handling */
  flex: 1 1 0 !important;
  min-width: 0 !important;
  min-height: 48px !important;
  white-space: normal !important;
  text-transform: none !important;
  line-height: 1.4 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  align-self: stretch !important;
  padding: $spacing-2 $spacing-3 !important;
}

/* Improve spacing on mobile */
@media (max-width: 576px) {
  .enteredAddress,
  .verifyaddressLine {
    margin-left: 15px;
  }
}
</style>
